// Pattern Detection Types

export enum PatternType {
  // Reversal Patterns
  HEAD_AND_SHOULDERS = 'head_and_shoulders',
  INVERSE_HEAD_AND_SHOULDERS = 'inverse_head_and_shoulders',
  DOUBLE_TOP = 'double_top',
  DOUBLE_BOTTOM = 'double_bottom',
  TRIPLE_TOP = 'triple_top',
  TRIPLE_BOTTOM = 'triple_bottom',

  // Continuation Patterns
  CUP_AND_HANDLE = 'cup_and_handle',
  ROUNDING_BOTTOM = 'rounding_bottom',
  BULLISH_FLAG = 'bullish_flag',
  BEARISH_FLAG = 'bearish_flag',
  BULLISH_PENNANT = 'bullish_pennant',
  BEARISH_PENNANT = 'bearish_pennant',

  // Triangles
  ASCENDING_TRIANGLE = 'ascending_triangle',
  DESCENDING_TRIANGLE = 'descending_triangle',
  SYMMETRIC_TRIANGLE = 'symmetric_triangle',

  // Wedges
  RISING_WEDGE = 'rising_wedge',
  FALLING_WEDGE = 'falling_wedge',

  // Candlestick
  DOJI = 'doji',
  HAMMER = 'hammer',
  SHOOTING_STAR = 'shooting_star',
  ENGULFING_BULLISH = 'engulfing_bullish',
  ENGULFING_BEARISH = 'engulfing_bearish',
}

export type PatternDirection = 'bullish' | 'bearish' | 'neutral';
export type PatternStatus = 'forming' | 'completed' | 'failed';

// Backend API response types (snake_case as returned by API)
export interface DetectedPatternResponse {
  id: string;
  pattern_type: PatternType;
  direction: PatternDirection;
  confidence: number;
  detected_at: string;
  start_date: string;
  end_date: string;
  price_levels: {
    neckline?: number;
    support?: number;
    resistance?: number;
    trendline?: number[];
  };
  metadata: {
    width?: number;
    height?: number;
    volume_confirm?: boolean;
  };
  status: PatternStatus;
}

export interface ReliabilityScoreResponse {
  score: number;
  grade: 'high' | 'medium' | 'low';
  factors: {
    symmetry: number;
    volume_confirmation: boolean;
    backtest_win_rate?: number;
    age_penalty?: number;
  };
}

export interface PatternDetectionResult {
  patterns: DetectedPatternResponse[];
  last_updated: string;
  total_count: number;
  bullish_count: number;
  bearish_count: number;
}

// Frontend use types (camelCase for internal use)
export interface DetectedPattern {
  id: string;
  patternType: PatternType;
  direction: PatternDirection;
  confidence: number;
  detectedAt: string;
  startDate: string;
  endDate: string;
  priceLevels: {
    neckline?: number;
    support?: number;
    resistance?: number;
    trendline?: number[];
  };
  metadata: {
    width?: number;
    height?: number;
    volumeConfirm?: boolean;
  };
  status: PatternStatus;
}

// Converter from API response to frontend type
export function convertPatternResponse(pattern: DetectedPatternResponse): DetectedPattern {
  return {
    id: pattern.id,
    patternType: pattern.pattern_type,
    direction: pattern.direction,
    confidence: pattern.confidence,
    detectedAt: pattern.detected_at,
    startDate: pattern.start_date,
    endDate: pattern.end_date,
    priceLevels: {
      ...(pattern.price_levels?.neckline !== undefined && { neckline: pattern.price_levels?.neckline }),
      ...(pattern.price_levels?.support !== undefined && { support: pattern.price_levels?.support }),
      ...(pattern.price_levels?.resistance !== undefined && { resistance: pattern.price_levels?.resistance }),
      ...(pattern.price_levels?.trendline !== undefined && { trendline: pattern.price_levels?.trendline }),
    },
    metadata: {
      ...(pattern.metadata?.width !== undefined && { width: pattern.metadata?.width }),
      ...(pattern.metadata?.height !== undefined && { height: pattern.metadata?.height }),
      ...(pattern.metadata?.volume_confirm !== undefined && { volumeConfirm: pattern.metadata?.volume_confirm }),
    },
    status: pattern.status,
  };
}

// Converter from API response (snake_case) to frontend type (camelCase)
export function convertPatternDetectionResult(result: PatternDetectionResult): PatternDetectionData {
  return {
    patterns: result.patterns.map(convertPatternResponse),
    lastUpdated: result.last_updated,
    totalPatterns: result.total_count,
    bullishCount: result.bullish_count,
    bearishCount: result.bearish_count,
  };
}

// Frontend camelCase type for pattern detection results
export interface PatternDetectionData {
  patterns: DetectedPattern[];
  lastUpdated: string;
  totalPatterns: number;
  bullishCount: number;
  bearishCount: number;
}

export interface ReliabilityScore {
  score: number;              // 0-100
  grade: 'high' | 'medium' | 'low';
  factors: {
    symmetry: number;        // Pattern symmetry 0-100
    volumeConfirmation: boolean;
    backtestWinRate?: number;
    agePenalty?: number;     // Older patterns score lower
  };
}

export interface PatternFilters {
  type?: PatternType[];
  minConfidence?: number;
  status?: PatternStatus[];
  direction?: PatternDirection[];
}

// Pattern overlay colors
export const PATTERN_COLORS = {
  bullish: '#22c55e',        // green-500
  bearish: '#ef4444',        // red-500
  neutral: '#6366f1',        // indigo-500
} as const;

// Helper functions
export function getPatternName(patternType: PatternType): string {
  const names: Record<PatternType, string> = {
    [PatternType.HEAD_AND_SHOULDERS]: 'Head & Shoulders',
    [PatternType.INVERSE_HEAD_AND_SHOULDERS]: 'Inverse Head & Shoulders',
    [PatternType.DOUBLE_TOP]: 'Double Top',
    [PatternType.DOUBLE_BOTTOM]: 'Double Bottom',
    [PatternType.TRIPLE_TOP]: 'Triple Top',
    [PatternType.TRIPLE_BOTTOM]: 'Triple Bottom',
    [PatternType.CUP_AND_HANDLE]: 'Cup & Handle',
    [PatternType.ROUNDING_BOTTOM]: 'Rounding Bottom',
    [PatternType.BULLISH_FLAG]: 'Bullish Flag',
    [PatternType.BEARISH_FLAG]: 'Bearish Flag',
    [PatternType.BULLISH_PENNANT]: 'Bullish Pennant',
    [PatternType.BEARISH_PENNANT]: 'Bearish Pennant',
    [PatternType.ASCENDING_TRIANGLE]: 'Ascending Triangle',
    [PatternType.DESCENDING_TRIANGLE]: 'Descending Triangle',
    [PatternType.SYMMETRIC_TRIANGLE]: 'Symmetric Triangle',
    [PatternType.RISING_WEDGE]: 'Rising Wedge',
    [PatternType.FALLING_WEDGE]: 'Falling Wedge',
    [PatternType.DOJI]: 'Doji',
    [PatternType.HAMMER]: 'Hammer',
    [PatternType.SHOOTING_STAR]: 'Shooting Star',
    [PatternType.ENGULFING_BULLISH]: 'Bullish Engulfing',
    [PatternType.ENGULFING_BEARISH]: 'Bearish Engulfing',
  };
  return names[patternType] || patternType;
}

export function getPatternColor(direction: PatternDirection): string {
  return PATTERN_COLORS[direction];
}

export function getReliabilityGrade(score: number): 'high' | 'medium' | 'low' {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

export function isBullishPattern(patternType: PatternType): boolean {
  const bullishPatterns = [
    PatternType.INVERSE_HEAD_AND_SHOULDERS,
    PatternType.DOUBLE_BOTTOM,
    PatternType.TRIPLE_BOTTOM,
    PatternType.CUP_AND_HANDLE,
    PatternType.ROUNDING_BOTTOM,
    PatternType.BULLISH_FLAG,
    PatternType.BULLISH_PENNANT,
    PatternType.ASCENDING_TRIANGLE,
    PatternType.FALLING_WEDGE,
    PatternType.HAMMER,
    PatternType.ENGULFING_BULLISH,
  ];
  return bullishPatterns.includes(patternType);
}
